import * as RDialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import type { ComponentProps, ReactNode } from 'react'
import { cn } from './cn'
import { IconButton } from './IconButton'
import { usePrivateAttrs } from './privateArea'
import { useReturnFocusToOpener } from './returnFocus'
import { t } from '@/lib/i18n'

export type DialogSize = 'sm' | 'md' | 'lg' | 'xl'

export const DialogRoot = RDialog.Root
export const DialogTrigger = RDialog.Trigger
export const DialogClose = RDialog.Close

const widths: Record<DialogSize, string> = {
  sm: 'sm:max-w-[420px]',
  md: 'sm:max-w-[560px]',
  lg: 'sm:max-w-[800px]',
  xl: 'sm:max-w-[1040px]',
}

export interface DialogContentProps extends ComponentProps<typeof RDialog.Content> {
  size?: DialogSize
  /** Hide the X button (the header still renders the title). */
  hideClose?: boolean
  overlayClassName?: string
}

/**
 * `data-escape-local` on a control that handles Escape itself (a video comment box clears its draft): Escape pressed
 * inside it stays there instead of closing the dialog around it; the next Escape, with focus elsewhere, closes the
 * dialog. Radix dismisses from a document capture listener, before the control's own key handler could stop it
 * (video-comments-2).
 */
function keepLocalEscape(e: KeyboardEvent) {
  if (e.target instanceof Element && e.target.closest('[data-escape-local]')) e.preventDefault()
}

/**
 * Centered modal panel on desktop; below 640px it becomes a bottom sheet
 * (full height for lg and xl). Esc and overlay click close it (Esc not from a data-escape-local control).
 */
export function DialogContent({ size = 'md', hideClose, className, overlayClassName, children, onOpenAutoFocus, onCloseAutoFocus, onEscapeKeyDown, ...rest }: DialogContentProps) {
  const tall = size === 'lg' || size === 'xl'
  // E2E-J8-06: focus goes back to whatever opened the dialog, with or without a DialogTrigger.
  const focus = useReturnFocusToOpener(onOpenAutoFocus, onCloseAutoFocus)
  const priv = usePrivateAttrs() // opened inside a private area: masked like it (docs/analytics.md)
  return (
    <RDialog.Portal>
      <RDialog.Overlay
        className={cn(
          'fixed inset-0 z-50 flex justify-center overflow-y-auto bg-overlay backdrop-blur-[2px]',
          // sm:py-6 matches the panel's sm:max-h (100dvh - 48px): a bounded panel always fits, so the overlay has
          // nothing to scroll and a tall dialog's footer is never pushed below the fold.
          'items-end sm:items-start sm:px-6 sm:py-6',
          'data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out',
          overlayClassName,
        )}
      >
        <RDialog.Content
          className={cn(
            // The panel is the scroll container (never the overlay), so a tall sheet cannot open scrolled away.
            // Overscroll is contained on the phone sheet only: on desktop a panel that grows past the viewport
            // (sm:max-h-none, the overlay scrolls) must hand the wheel and PageDown on to the overlay (Q-UX-26).
            'relative flex w-full flex-col overflow-y-auto max-sm:overscroll-contain bg-surface text-text shadow-pop outline-none',
            'rounded-t-modal max-sm:max-h-[calc(100dvh-24px)] max-sm:pb-[env(safe-area-inset-bottom)]',
            'max-sm:data-[state=open]:animate-sheet-up max-sm:data-[state=closed]:animate-sheet-down',
            tall && 'max-sm:h-[calc(100dvh-8px)] max-sm:max-h-none',
            // Grab handle hint on the mobile sheet.
            'max-sm:before:absolute max-sm:before:top-2 max-sm:before:left-1/2 max-sm:before:h-1 max-sm:before:w-9',
            'max-sm:before:-translate-x-1/2 max-sm:before:rounded-full max-sm:before:bg-line-strong',
            'sm:my-auto sm:max-h-[calc(100dvh-48px)] sm:rounded-modal sm:data-[state=open]:animate-dialog-in sm:data-[state=closed]:animate-dialog-out',
            widths[size],
            className,
          )}
          {...priv}
          {...rest}
          onOpenAutoFocus={focus.onOpenAutoFocus}
          onCloseAutoFocus={focus.onCloseAutoFocus}
          onEscapeKeyDown={(e) => {
            onEscapeKeyDown?.(e)
            keepLocalEscape(e)
          }}
        >
          {children}
          {!hideClose && (
            <RDialog.Close asChild>
              <IconButton aria-label={t('Close')} icon={<X />} size={36} className="absolute top-3 right-3 z-10" />
            </RDialog.Close>
          )}
        </RDialog.Content>
      </RDialog.Overlay>
    </RDialog.Portal>
  )
}

export interface DialogHeaderProps {
  title: ReactNode
  description?: ReactNode
  /** Keep the title for screen readers only. */
  hideTitle?: boolean
  className?: string
}

export function DialogHeader({ title, description, hideTitle, className }: DialogHeaderProps) {
  return (
    <div className={cn('shrink-0 px-5 pt-5 pr-14 sm:px-6 sm:pt-5', hideTitle && 'sr-only', className)}>
      <RDialog.Title className="text-[18px] leading-7 font-bold tracking-[-0.01em] text-text">{title}</RDialog.Title>
      {description && (typeof description === 'string' || typeof description === 'number') ? (
        <RDialog.Description className="mt-1 text-body text-muted">{description}</RDialog.Description>
      ) : description ? (
        // Markup (chips, lists, fields) renders in a div, so block content is never a div inside a p (Q-COMM-35).
        <RDialog.Description asChild>
          <div className="mt-1 text-body text-muted">{description}</div>
        </RDialog.Description>
      ) : null}
    </div>
  )
}

export function DialogBody({ className, ...rest }: ComponentProps<'div'>) {
  // relative: hidden form inputs that Radix positions absolutely (Switch, Checkbox, Select) stay inside
  // the scrolling body instead of stretching the panel (Q-UX-11).
  return <div className={cn('relative min-h-0 flex-1 overflow-y-auto px-5 py-4 sm:px-6', className)} {...rest} />
}

export function DialogFooter({ className, ...rest }: ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'flex shrink-0 flex-col-reverse gap-2 border-t border-line px-5 py-4 sm:flex-row sm:items-center sm:justify-end sm:px-6',
        className,
      )}
      {...rest}
    />
  )
}

export const DialogTitle = RDialog.Title
export const DialogDescription = RDialog.Description

export interface DialogProps {
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  /** Optional trigger element (rendered with asChild). */
  trigger?: ReactNode
  title: ReactNode
  description?: ReactNode
  hideTitle?: boolean
  size?: DialogSize
  /** Action row at the bottom (right aligned on desktop, stacked on mobile). */
  footer?: ReactNode
  hideClose?: boolean
  /** Keep the dialog open on overlay click (Esc still closes). */
  dismissible?: boolean
  className?: string
  bodyClassName?: string
  children?: ReactNode
}

/** Modal dialog with a title, close button, scrollable body and optional footer. */
export function Dialog({
  open,
  defaultOpen,
  onOpenChange,
  trigger,
  title,
  description,
  hideTitle,
  size = 'md',
  footer,
  hideClose,
  dismissible = true,
  className,
  bodyClassName,
  children,
}: DialogProps) {
  return (
    <RDialog.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
      {trigger && <RDialog.Trigger asChild>{trigger}</RDialog.Trigger>}
      <DialogContent
        size={size}
        hideClose={hideClose}
        className={className}
        onPointerDownOutside={dismissible ? undefined : (e) => e.preventDefault()}
        {...(description ? {} : { 'aria-describedby': undefined })}
      >
        <DialogHeader title={title} description={description} hideTitle={hideTitle} />
        {children != null && <DialogBody className={bodyClassName}>{children}</DialogBody>}
        {footer && <DialogFooter>{footer}</DialogFooter>}
      </DialogContent>
    </RDialog.Root>
  )
}
